import React, { useState, useEffect, useCallback } from 'react';
import condicaoPagamentoService from '../../services/condicaoPagamento.service';
import BtnIcone from '../../components/BtnIcone';
import SePode from '../../components/SePode';   // r88: botões condicionais por permissão
import Modal from '../../components/Modal';
import BrowseHeader from '../../components/BrowseHeader';                    // r110
import { iconeDaRotina } from '../../constants/iconesRotinas';               // r110

const COR = {
  azul:     '#003399',
  azulBg:   '#eff6ff',
  borda:    '#e8eaed',
  texto:    '#1a1a2e',
  muted:    '#888',
  branco:   '#fff',
  verde:    '#1D9E75',
  verdeBg:  '#f0fff4',
  vermelho: '#e53935',
  vermBg:   '#fff0f0',
  amarelo:  '#d97706',
  amlBg:    '#fffbeb',
};

const inputStyle = {
  width: '100%', height: '36px', padding: '0 10px',
  border: `1px solid ${COR.borda}`, borderRadius: '5px',
  fontSize: '13px', boxSizing: 'border-box',
  backgroundColor: COR.branco, color: COR.texto, outline: 'none',
};

const inputDisabledStyle = {
  ...inputStyle, backgroundColor: '#fafafa', color: COR.muted,
};

// ─── Helpers ────────────────────────────────────────────────────────────────
function StatusPill({ status }) {
  const ativo = status === 'A';
  const cor = ativo ? COR.verde : COR.amarelo;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px',
      borderRadius: '20px', fontSize: '12px', fontWeight: '500',
      backgroundColor: ativo ? COR.verdeBg : COR.amlBg,
      color: cor,
      border: `1px solid ${cor}`,           // r110: borda na cor da fonte
    }}>{ativo ? 'Ativo' : 'Inativo'}</span>
  );
}

function Campo({ label, children, obrigatorio }) {
  return (
    <div style={{ marginBottom: '14px' }}>
      <label style={{
        display: 'block', fontSize: '12px', fontWeight: '600',
        color: COR.muted, marginBottom: '4px',
      }}>
        {label}{obrigatorio && <span style={{ color: COR.vermelho }}> *</span>}
      </label>
      {children}
    </div>
  );
}

// Parseia "0, 15, 30" em [0, 15, 30] (mostra preview de parcelas)
function parsearDias(s) {
  if (!s) return [];
  return String(s)
    .split(/[,;]/)
    .map(x => x.trim())
    .filter(x => x !== '')
    .map(x => parseInt(x, 10))
    .filter(n => !isNaN(n) && n >= 0);
}

const FORM_VAZIO = {
  condicao: '',
  descricao: '',
  status: 'A',
};

function sanitizar(obj) {
  return Object.fromEntries(
    Object.entries(obj).map(([k, v]) => [k, v === null || v === undefined ? '' : v])
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
function CondicoesPagamentoPage() {
  const [lista, setLista]             = useState([]);
  const [carregando, setCarregando]   = useState(true);
  const [busca, setBusca]             = useState('');
  const [ordenacao, setOrdenacao]     = useState({ campo: 'descricao', dir: 'asc' });

  const [painel, setPainel]           = useState(null);
  const [selecionado, setSelecionado] = useState(null);
  const [form, setForm]               = useState(FORM_VAZIO);
  const [salvando, setSalvando]       = useState(false);
  const [erroPainel, setErroPainel]   = useState('');
  const [sucessoPainel, setSucessoPainel] = useState('');

  // Usuário logado (pra auditoria)
  const usuarioLogadoId = (() => {
    try {
      const raw = localStorage.getItem('usuario');
      return raw ? JSON.parse(raw).id : null;
    } catch { return null; }
  })();

  // ── Carregar ──────────────────────────────────────────────────────────────
  const carregar = useCallback(async () => {
    try {
      setCarregando(true);
      const dados = await condicaoPagamentoService.listar();
      setLista(dados);
    } catch {
      setLista([]);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  // ── Filtro e ordenação ────────────────────────────────────────────────────
  const listaFiltrada = lista
    .filter(c => {
      const q = busca.toLowerCase();
      return !q
        || c.descricao.toLowerCase().includes(q)
        || c.condicao.toLowerCase().includes(q);
    })
    .sort((a, b) => {
      const va = (a[ordenacao.campo] || '').toString().toLowerCase();
      const vb = (b[ordenacao.campo] || '').toString().toLowerCase();
      return ordenacao.dir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
    });

  const toggleOrdem = (campo) =>
    setOrdenacao(o => ({ campo, dir: o.campo === campo && o.dir === 'asc' ? 'desc' : 'asc' }));

  const setaCabecalho = (campo) => {
    if (ordenacao.campo !== campo) return ' ↕';
    return ordenacao.dir === 'asc' ? ' ↑' : ' ↓';
  };

  // ── Ações ─────────────────────────────────────────────────────────────────
  const abrirNovo = () => {
    setForm(FORM_VAZIO);
    setSelecionado(null);
    setErroPainel(''); setSucessoPainel('');
    setPainel('novo');
  };

  const abrirVisualizar = (c) => {
    setSelecionado(c);
    setForm(sanitizar({ ...FORM_VAZIO, ...c }));
    setErroPainel(''); setSucessoPainel('');
    setPainel('visualizar');
  };

  const abrirEditar = (c) => {
    setSelecionado(c);
    setForm(sanitizar({ ...FORM_VAZIO, ...c }));
    setErroPainel(''); setSucessoPainel('');
    setPainel('editar');
  };

  const fecharPainel = () => { setPainel(null); setSelecionado(null); };

  const handleCampo = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const alterarStatus = async (c) => {
    const novoStatus = c.status === 'A' ? 'I' : 'A';
    const acao = novoStatus === 'A' ? 'reativar' : 'inativar';
    if (!window.confirm(`Tem certeza que deseja ${acao} a condição "${c.descricao}"?`)) return;
    try {
      await condicaoPagamentoService.alterarStatus(c.id, novoStatus, usuarioLogadoId);
      await carregar();
    } catch (err) {
      alert(err.response?.data?.mensagem || 'Erro ao alterar status.');
    }
  };

  // ── Salvar ────────────────────────────────────────────────────────────────
  const salvar = async () => {
    setErroPainel('');
    if (!form.condicao || !form.condicao.trim())
      return setErroPainel('Campo "Condição" é obrigatório.');
    if (!form.descricao || !form.descricao.trim())
      return setErroPainel('Campo "Descrição" é obrigatório.');

    const dias = parsearDias(form.condicao);
    if (dias.length === 0)
      return setErroPainel('Condição deve conter ao menos um número (ex: "0, 15, 30").');

    try {
      setSalvando(true);
      const payload = {
        condicao: form.condicao,
        descricao: form.descricao,
        usuario_id: usuarioLogadoId,
      };
      if (painel === 'novo') {
        await condicaoPagamentoService.incluir(payload);
      } else {
        await condicaoPagamentoService.alterar(selecionado.id, payload);
      }
      setSucessoPainel(painel === 'novo'
        ? 'Condição incluída com sucesso!'
        : 'Condição alterada com sucesso!');
      await carregar();
      setTimeout(fecharPainel, 1200);
    } catch (err) {
      setErroPainel(err.response?.data?.mensagem || 'Erro ao salvar.');
    } finally {
      setSalvando(false);
    }
  };

  const somenteLeitura = painel === 'visualizar';

  // ── Preview de parcelas (calculado em tempo real) ──────────────────────────
  const diasPreview = parsearDias(form.condicao);

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div style={{ fontFamily: "'Manrope', sans-serif" }}>

      {/* r110: BrowseHeader padronizado no topo */}
      <BrowseHeader
        rotina="condicoes-pagamento"
        titulo="Condições de Pagamento"
        descricao="Modalidades de prazo e parcelamento usadas nas propostas comerciais."
      />

      {/* ── BROWSE ── */}
      <div style={{
        backgroundColor: COR.branco,
        border: `1px solid ${COR.borda}`, borderRadius: '8px',
        overflow: 'hidden',
      }}>
        <div style={{
          padding: '14px 16px', borderBottom: `1px solid ${COR.borda}`,
          display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: '13px', fontWeight: '500', color: COR.muted, flex: 1 }}>
            {listaFiltrada.length} registro{listaFiltrada.length !== 1 ? 's' : ''}
          </span>
          <input
            type="text" placeholder="Pesquisar..."
            value={busca} onChange={e => setBusca(e.target.value)}
            style={{ ...inputStyle, width: '352px' }}
          />
          <SePode rotina="condicoes-pagamento" acao="incluir"><BtnIcone icone="incluir" onClick={abrirNovo} title="Incluir nova condição" cor={COR.azul} solido tamanho="grande" /></SePode>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ backgroundColor: COR.azul }}>
                {/* r110: 1ª coluna fixa pro ícone SVG da rotina */}
                <th style={{ padding: '10px 14px', width: '36px' }}></th>
                {[
                  { label: 'Descrição',     campo: 'descricao'    },
                  { label: 'Condição',      campo: 'condicao'     },
                  { label: 'Qtd. parcelas', campo: 'qtd_parcelas' },
                  { label: 'Status',        campo: 'status'       },
                  { label: 'Ações',         campo: null           },
                ].map(({ label, campo }) => (
                  <th key={label} onClick={() => campo && toggleOrdem(campo)} style={{
                    padding: '10px 14px', textAlign: 'left',
                    fontSize: '13px', fontWeight: '700', color: COR.branco,
                    whiteSpace: 'nowrap', cursor: campo ? 'pointer' : 'default', userSelect: 'none',
                  }}>
                    {label}{campo && setaCabecalho(campo)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {carregando ? (
                <tr><td colSpan="6" style={{ padding: '40px', textAlign: 'center', color: COR.muted }}>Carregando...</td></tr>
              ) : listaFiltrada.length === 0 ? (
                <tr><td colSpan="6" style={{ padding: '40px', textAlign: 'center', color: COR.muted }}>Nenhuma condição encontrada.</td></tr>
              ) : listaFiltrada.map((c, i) => (
                <tr key={c.id} onClick={() => abrirVisualizar(c)} style={{
                  borderBottom: `1px solid ${COR.borda}`,
                  backgroundColor: selecionado?.id === c.id ? COR.azulBg : i % 2 === 0 ? COR.branco : '#fafafa',
                  cursor: 'pointer',
                }}>
                  {/* r110: ícone SVG #003399 da rotina (mesmo do menu lateral) */}
                  <td style={{ padding: '10px 0 10px 14px', verticalAlign: 'middle' }}>
                    {iconeDaRotina('condicoes-pagamento', { width: 20, height: 20, color: COR.azul })}
                  </td>
                  <td style={{ padding: '10px 14px', fontWeight: '500', color: COR.texto }}>{c.descricao}</td>
                  <td style={{ padding: '10px 14px', color: COR.muted, fontVariantNumeric: 'tabular-nums' }}>{c.condicao}</td>
                  <td style={{ padding: '10px 14px', color: COR.muted, textAlign: 'center' }}>{c.qtd_parcelas}</td>
                  <td style={{ padding: '10px 14px' }}><StatusPill status={c.status} /></td>
                  <td style={{ padding: '10px 14px' }}>
                    <div style={{ display: 'flex', gap: '4px' }} onClick={ev => ev.stopPropagation()}>
                      <SePode rotina="condicoes-pagamento" acao="alterar"><BtnIcone icone="alterar" onClick={() => abrirEditar(c)} title="Alterar" cor={COR.azul} /></SePode>
                      <SePode rotina="condicoes-pagamento" acao="alterar"><BtnIcone
                        icone={c.status === 'A' ? 'bloquear' : 'desbloquear'}
                        onClick={() => alterarStatus(c)}
                        title={c.status === 'A' ? 'Inativar' : 'Ativar'}
                        cor={c.status === 'A' ? COR.vermelho : COR.verde}
                      /></SePode>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── MODAL ── */}
      <Modal
        aberto={!!painel}
        titulo={
          painel === 'novo'   ? 'Nova Condição de Pagamento' :
          painel === 'editar' ? 'Alterar Condição de Pagamento' :
                                'Visualizar Condição de Pagamento'
        }
        onFechar={fecharPainel}
        onSalvar={salvar}
        salvando={salvando}
        somenteLeitura={somenteLeitura}
        onEditar={() => setPainel('editar')}
      >
        <div>
          {erroPainel && (
            <div style={{
              backgroundColor: COR.vermBg, border: '1px solid #ffcccc',
              borderRadius: '5px', color: COR.vermelho, fontSize: '13px',
              padding: '10px 12px', marginBottom: '12px',
            }}>{erroPainel}</div>
          )}
          {sucessoPainel && (
            <div style={{
              backgroundColor: COR.verdeBg, border: '1px solid #a8e6c1',
              borderRadius: '5px', color: '#1d6a3a', fontSize: '13px',
              padding: '10px 12px', marginBottom: '12px',
            }}>{sucessoPainel}</div>
          )}

          <Campo label="Descrição" obrigatorio>
            <input
              name="descricao"
              value={form.descricao}
              onChange={handleCampo}
              disabled={somenteLeitura}
              style={somenteLeitura ? inputDisabledStyle : inputStyle}
              placeholder='Ex: "00 + 15 + 30 Dias"'
              maxLength={120}
            />
          </Campo>

          <Campo label="Condição (dias separados por vírgula)" obrigatorio>
            <input
              name="condicao"
              value={form.condicao}
              onChange={handleCampo}
              disabled={somenteLeitura}
              style={{ ...((somenteLeitura ? inputDisabledStyle : inputStyle)), fontVariantNumeric: 'tabular-nums' }}
              placeholder='Ex: "0, 15, 30"'
              maxLength={200}
            />
            <div style={{ fontSize: '11px', color: COR.muted, marginTop: '4px' }}>
              Cada número representa quantos dias após a data de faturamento a parcela vence.
              <br />Ex: "0, 15, 30" = 1ª parcela na data, 2ª em 15 dias, 3ª em 30 dias.
            </div>
          </Campo>

          {/* Preview das parcelas */}
          {diasPreview.length > 0 && (
            <div style={{
              backgroundColor: COR.azulBg, border: `1px solid ${COR.azul}33`,
              borderRadius: '6px', padding: '10px 12px', marginTop: '8px',
            }}>
              <div style={{
                fontSize: '11px', fontWeight: '700', color: COR.azul,
                textTransform: 'uppercase', letterSpacing: '0.5px',
                marginBottom: '8px',
              }}>
                📋 Preview ({diasPreview.length} parcela{diasPreview.length !== 1 ? 's' : ''})
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {diasPreview.map((d, i) => (
                  <span key={i} style={{
                    display: 'inline-block', padding: '4px 10px',
                    backgroundColor: COR.branco, border: `1px solid ${COR.azul}55`,
                    borderRadius: '4px', fontSize: '12px', fontWeight: '600',
                    color: COR.azul,
                  }}>
                    Parcela {i + 1}: {d === 0 ? 'na data' : `+${d} dias`}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}

export default CondicoesPagamentoPage;
